import { useEffect, useRef, useState } from 'react';
import { GOOGLE_CLIENT_ID, IS_GOOGLE_AUTH_ENABLED } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  width?: number;
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  ux_mode?: 'popup' | 'redirect';
}

interface GoogleAccountsId {
  initialize: (configuration: GoogleIdConfiguration) => void;
  renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

export type GoogleSignInButtonAlign = 'start' | 'center' | 'end';

interface GoogleSignInButtonProps {
  className?: string;
  align?: GoogleSignInButtonAlign;
}

const GOOGLE_BUTTON_WIDTH = 220;

const alignmentClasses: Record<GoogleSignInButtonAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
};

export function GoogleSignInButton({
  className = '',
  align = 'end',
}: GoogleSignInButtonProps): JSX.Element | null {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { signInWithGoogle, isSigningIn } = useAuth();

  useEffect(() => {
    if (!IS_GOOGLE_AUTH_ENABLED) return;

    let cancelled = false;
    const renderButton = (): void => {
      if (cancelled || !buttonRef.current || !window.google?.accounts.id) return;
      buttonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: GoogleCredentialResponse) => {
          if (!response.credential) {
            setError('Google did not return a credential');
            return;
          }
          setError(null);
          void signInWithGoogle(response.credential).catch((signInError: unknown) => {
            setError(signInError instanceof Error ? signInError.message : 'Google sign-in failed');
          });
        },
        ux_mode: 'popup',
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: GOOGLE_BUTTON_WIDTH,
      });
    };

    if (window.google?.accounts.id) {
      renderButton();
      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', renderButton);
      return () => {
        cancelled = true;
        existingScript.removeEventListener('load', renderButton);
      };
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', renderButton);
    script.addEventListener('error', () => {
      if (!cancelled) setError('Google sign-in failed to load');
    });
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener('load', renderButton);
    };
  }, [signInWithGoogle]);

  if (!IS_GOOGLE_AUTH_ENABLED) {
    return null;
  }

  const buttonFrameClass = 'w-[220px]';

  return (
    <div className={`flex flex-col ${alignmentClasses[align]} gap-1 ${className}`}>
      <div className={buttonFrameClass} title="Continue with Google">
        <div
          ref={buttonRef}
          className={`${isSigningIn ? 'pointer-events-none opacity-60' : ''} w-[220px]`}
        />
      </div>
      {error && (
        <p className="max-w-[220px] text-right text-xs font-medium text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
